import {DisplayItem} from './_models'

export interface PageEntry {
  id: string
  title: string
  subtitle: string
  url: string
  icon?: string
  /** Kategori yang bersesuaian dengan tab global search */
  category?: string
  /** Akses dicek sama persis dengan PrivateRoutes.tsx */
  canAccess: (has: (key: string) => boolean, roleName: string) => boolean
  keywords: string[]
}

const isSuper = (r: string) => r === 'ADMIN' || r === 'SUPERADMIN'

/**
 * Index seluruh halaman valid aplikasi Smart Billing yang terdaftar di PrivateRoutes.
 */
const PAGE_INDEX: PageEntry[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    subtitle: 'Ringkasan trafik, revenue & performa billing',
    url: '/dashboard',
    icon: '📊',
    category: 'Dashboard',
    canAccess: (h, r) => h('dashboard') || isSuper(r),
    keywords: ['home', 'beranda', 'ringkasan', 'statistik', 'analytics', 'wallboard'],
  },
  {
    id: 'clients',
    title: 'Clients',
    subtitle: 'Manajemen data pelanggan, NPWP, kontak & status',
    url: '/clients',
    icon: '🏢',
    category: 'Clients',
    canAccess: (h) => h('clients'),
    keywords: ['pelanggan', 'customer', 'klien', 'npwp', 'perusahaan', 'kontak'],
  },
  {
    id: 'billing',
    title: 'Billing Rates',
    subtitle: 'Tarif dasar provider, prefix tujuan & rate per detik/menit',
    url: '/billing',
    icon: '💵',
    category: 'Billing Rates',
    canAccess: (h) => h('billing_rates'),
    keywords: ['tarif', 'rate', 'harga', 'provider', 'prefix', 'telkomsel', 'indosat', 'xl', 'smartfren', 'tri'],
  },
  {
    id: 'products',
    title: 'Products & Services',
    subtitle: 'Katalog produk, paket langganan & bundle layanan',
    url: '/products',
    icon: '📦',
    category: 'Products',
    canAccess: (h, r) => h('products') || h('billing_rates') || isSuper(r),
    keywords: ['produk', 'layanan', 'service', 'katalog', 'sip trunk', 'saas', 'paket', 'bundle'],
  },
  {
    id: 'cdr',
    title: 'CDR Report',
    subtitle: 'Laporan rekaman detail panggilan dan durasi pemakaian',
    url: '/cdr',
    icon: '📞',
    category: 'CDR',
    canAccess: (h) => h('cdr_report'),
    keywords: ['call detail', 'panggilan', 'trafik', 'laporan', 'report', 'telepon', 'durasi', 'call log', 'cdr'],
  },
  {
    id: 'cdr-import',
    title: 'CDR Import',
    subtitle: 'Import data file CDR (CSV / Excel) ke sistem',
    url: '/cdr/import',
    icon: '📥',
    category: 'CDR',
    canAccess: (h) => h('cdr_report'),
    keywords: ['upload', 'import', 'csv', 'excel', 'xlsx', 'file cdr', 'unggah', 'cdr'],
  },
  {
    id: 'data-sources',
    title: 'Data Sources',
    subtitle: 'Konfigurasi koneksi database CDR, pemetaan kolom & scheduler',
    url: '/data-sources',
    icon: '🗄️',
    category: 'Data Sources',
    canAccess: (h, r) => h('data_source') || h('cdr_report') || isSuper(r),
    keywords: ['database', 'sync', 'sinkron', 'mysql', 'postgres', 'cron', 'jadwal', 'worker', 'koneksi'],
  },
  {
    id: 'invoice',
    title: 'Invoices',
    subtitle: 'Daftar invoice, kalkulasi pemakaian, status bayar & cetak faktur',
    url: '/invoice',
    icon: '📄',
    category: 'Invoices',
    canAccess: (h) => h('invoice'),
    keywords: ['tagihan', 'faktur', 'bill', 'template invoice', 'cetak', 'pdf', 'generate tagihan', 'grand total'],
  },
  {
    id: 'settings-company',
    title: 'Company Settings',
    subtitle: 'Profil identitas perusahaan, logo, rekening bank & kop surat',
    url: '/settings/company',
    icon: '🏛️',
    category: 'Global Settings',
    canAccess: (h) => h('global_settings'),
    keywords: ['perusahaan', 'logo', 'bank', 'alamat', 'kop surat', 'pengaturan perusahaan', 'company', 'global settings'],
  },
  {
    id: 'settings-system',
    title: 'System Settings',
    subtitle: 'Konfigurasi parameter Telkom, format no pelanggan & footer invoice',
    url: '/settings/system',
    icon: '⚙️',
    category: 'Global Settings',
    canAccess: (h) => h('global_settings'),
    keywords: ['nomor pelanggan', 'customer number', 'telkom', 'footer', 'konfigurasi sistem', 'system config', 'global settings'],
  },
  {
    id: 'activity-log',
    title: 'Activity Log',
    subtitle: 'Audit trail riwayat aktivitas pengguna, login & perubahan data',
    url: '/activity-log',
    icon: '📜',
    category: 'Activity Log',
    canAccess: (h, r) => h('activity_log') || isSuper(r) || r === 'CLIENT',
    keywords: ['audit', 'log', 'riwayat', 'history', 'notifikasi', 'jejak aktivitas', 'audit trail'],
  },
  {
    id: 'roles',
    title: 'Role Management',
    subtitle: 'Pengaturan hak akses, izin peran & menu privilege RBAC',
    url: '/roles',
    icon: '🛡️',
    category: 'Roles',
    canAccess: (h) => h('role_management'),
    keywords: ['role', 'akses', 'permission', 'izin', 'rbac', 'hak akses', 'otoritas'],
  },
  {
    id: 'users',
    title: 'User Management',
    subtitle: 'Kelola akun user, kredensial login, email & status aktif',
    url: '/users',
    icon: '👥',
    category: 'Users',
    canAccess: (h) => h('user_management'),
    keywords: ['user', 'pengguna', 'akun', 'login', 'username', 'email user', 'manajemen pengguna'],
  },
  {
    id: 'profile',
    title: 'My Profile',
    subtitle: 'Informasi akun pribadi, detail profil dan ganti kata sandi',
    url: '/profile',
    icon: '👤',
    category: 'Pages',
    canAccess: () => true,
    keywords: ['profil', 'akun', 'password', 'ganti password', 'my profile', 'user info'],
  },
]

export const getAccessiblePages = (allowedMenus: string[], roleName: string): PageEntry[] => {
  const has = (k: string) => allowedMenus.includes(k)
  return PAGE_INDEX.filter((p) => p.canAccess(has, roleName))
}

export const searchPages = (pages: PageEntry[], term: string, limit = 5): DisplayItem[] => {
  const q = term.trim().toLowerCase()
  if (!q) return []
  return pages
    .map((p) => {
      const title = p.title.toLowerCase()
      let score = 0
      if (title === q) score = 1
      else if (title.startsWith(q)) score = 0.85
      else if (title.includes(q)) score = 0.7
      else if (p.keywords.some((k) => k.startsWith(q))) score = 0.6
      else if (p.keywords.some((k) => k.includes(q)) || p.subtitle.toLowerCase().includes(q)) score = 0.4
      return {p, score}
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({p}) => ({
      key: `page-${p.id}`,
      category: 'Pages',
      title: p.title,
      subtitle: p.subtitle,
      url: p.url,
      icon: '🧭',
    }))
}

export const pagesAsQuickLinks = (pages: PageEntry[], limit = 20): DisplayItem[] =>
  pages.slice(0, limit).map((p) => ({
    key: `quick-${p.id}`,
    category: p.category || 'Pages',
    title: p.title,
    subtitle: p.subtitle,
    url: p.url,
    icon: p.icon || '🧭',
  }))
